import { useEffect, useState } from "react";
import { useNavigate } from "@/lib/router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { authApi } from "@/api/auth";
import { isWebAuthnSupported } from "@/lib/passkey";
import { queryKeys } from "@/lib/queryKeys";
import { useBreadcrumbs } from "@/context/BreadcrumbContext";
import { Fingerprint, Loader2 } from "lucide-react";
import { LiquidCardShowcase } from "./LiquidCardShowcase";
import { ThetaMark } from "./ThetaMark";
import "./LandingPage.css";

export function PublicDocumentPage({ path }: { path: string }) {
  return <iframe src={path} title="OTC legal document" className="fixed inset-0 h-full w-full border-0" />;
}

export function LandingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setBreadcrumbs, setDocumentTitleOverride } = useBreadcrumbs();
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  useEffect(() => {
    setBreadcrumbs([{ label: "OTC" }]);
    // A nonempty, invisible title keeps browsers from substituting the URL.
    setDocumentTitleOverride("\u200B");
    return () => { setBreadcrumbs([]); setDocumentTitleOverride(null); };
  }, [setBreadcrumbs, setDocumentTitleOverride]);

  const passkeyMutation = useMutation({
    mutationFn: () => authApi.signInPasskey(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.session });
      await queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.access.currentBoardAccess });
      navigate("/app");
    },
    onError: (err: Error) => {
      setPasskeyError(err.message || "Passkey authentication failed");
    },
  });

  const handleConnectPasskey = () => {
    setPasskeyError(null);
    if (!isWebAuthnSupported()) {
      setPasskeyError("WebAuthn is not supported in this browser");
      return;
    }
    passkeyMutation.mutate();
  };

  return (
    <div className="mars-landing">
      <a className="mars-skip-link" href="#mars-main">Skip to content</a>
      <header className="mars-header">
        <a href="/" className="mars-brand" aria-label="OTC home"><ThetaMark /></a>
        <button className="mars-passkey" onClick={handleConnectPasskey} disabled={passkeyMutation.isPending} aria-describedby={passkeyError ? "mars-passkey-error" : undefined}>
          {passkeyMutation.isPending ? <Loader2 className="mars-spin" size={16} aria-hidden="true" /> : <Fingerprint size={17} aria-hidden="true" />}
          {passkeyMutation.isPending ? "Connecting…" : "Connect Passkey"}
        </button>
      </header>
      {passkeyError && <p className="mars-auth-error" role="alert" id="mars-passkey-error">{passkeyError}</p>}
      <main id="mars-main" tabIndex={-1}>
        <div className="otc-intro">
          <h1>One passkey.<br />More connections.</h1>
          <p>Meet. Talk. Buy. Connect. It starts with you.</p>
        </div>
        <LiquidCardShowcase />
        <div className="otc-entry">
          <a href="/auth" className="otc-entry-link">Get started with your passkey <span aria-hidden="true">↗</span></a>
          <details><summary>What is a passkey?</summary><p>Your secure key to OTC. Sign in using Face ID, Touch ID, or your device lock instead of remembering a password.</p></details>
        </div>
      </main>
      <footer className="mars-footer"><span><ThetaMark /></span><a href="/privacy">Privacy</a><a href="/terms">Terms</a></footer>
    </div>
  );
}
